import React from "react";
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Add, ArrowDropDown, ArrowDropUp, Business, Delete, Edit, Help, Star, StarBorder } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
    HtmlTooltip,
    HtmlTooltipDisabled,
    IconContainer,
    IconText,
    SpaceBetweenStack,
    StyledStack,
} from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { ListingCellCenter, ListingCellLeft, ListingCellRight, ListingHeaderContent, ListingIconMetric, ListingMetricValue, StyledTableCell } from "../../../components/commonComponents/TabularLists/styles";
import { canAssignOrMarkFavorite, checkUserDetailsFromCookie, checkUserPermissions, deleteUser, deleteUserFromSpecificCompany, manageUsersPaginationCookie, markFavoriteUser, resetUserData } from "../../../api_calls/userManagement";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { toast } from "react-toastify";
import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";
import Pagination from "../../../components/commonComponents/LoadMore/Pagination";
import { createCookie, deleteCookie } from "../../../api_calls/requestConfig";
import AddBrandUser from "../modalContent/AddBrandUser";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";
import { SortIcon } from "../../../api_calls/utils";

const BrandUserlist = ({ usedFor }) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [modalDetails, setModalDetails] = React.useState({});
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [userToDelete, setUserToDelete] = React.useState({});
    const [fieldError, setFieldError] = React.useState({});
    const [showMultiCompanyConfirmation, setShowMultiCompanyConfirmation] = React.useState(false);

    const [userToDeleteFromCompany, setUserToDeleteFromCompany] = React.useState({});
    const [searchVal, setSearchVal] = React.useState({ value: "" });
    const dispatchAction = useDispatch();
    const allowedAccessRights = checkUserPermissions();
    const permissionScope = allowedAccessRights.allowed_access;

    const checkUserDetails = checkUserDetailsFromCookie();
    const companyAllowedUserTypes = ["cyreen", "branch"];

    const currentStateData = useSelector((state) => state.userSelections);

    // sorting measures for files listing
    const [sortingMeasure, setSortingMeasure] = React.useState({
        username: "asc",
        email: "asc",
        company: "asc",
    });

    const [sortingParamName, setSortingParamName] = React.useState("username");

    const paginationStartValue =
        typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_start : 0;
    const paginationEndValue =
        typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_end : 10;

    const userRecords = typeof currentStateData.brand_users_list !== "undefined" && currentStateData.brand_users_list.length !== 0 ? currentStateData.brand_users_list : [];

    const applyFilter = () => {
        if (userRecords.length !== 0) {
            createCookie("brand_users_filter", JSON.stringify(searchVal.value));

            return sortedResults(
                userRecords.filter((user) => {
                    return (
                        user.username.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
                        user.email.toLowerCase().toString().includes(searchVal.value.toLowerCase().toString()) ||
                        user.invited_by.toLowerCase().toString().includes(searchVal.value.toLowerCase().toString())
                        ||
                        (companyAllowedUserTypes.includes(checkUserDetails.user_detail.user_type) && user.assigned_company_details.map((company, index) => {
                            return company.name
                        }).join(", ").toLowerCase().includes(searchVal.value.toLowerCase().toString())) ||
                        user.roles.join(",").toLowerCase().includes(searchVal.value.toLowerCase().toString())
                    );
                })
            );
        } else {
            return [];
        }
    };

    // to return the sorted list items
    const sortedResults = (toBeSortedList) => {
        return toBeSortedList.slice().sort((a, b) => {
            if (sortingParamName === "invited_by") {
                return sortingMeasure.invited_by === "asc"
                    ? a.invited_by.localeCompare(b.invited_by)
                    : b.invited_by.localeCompare(a.invited_by);
            } else if (sortingParamName === "email") {
                return sortingMeasure.email === "asc" ? a.email.localeCompare(b.email) : b.email.localeCompare(a.email);
            } else if (sortingParamName === "company") {
                const aCompanyNames = a.assigned_company_details.map((company) => company.name).sort().join(", ");
                const bCompanyNames = b.assigned_company_details.map((company) => company.name).sort().join(", ");
                return sortingMeasure.company === "asc"
                    ? aCompanyNames.localeCompare(bCompanyNames)
                    : bCompanyNames.localeCompare(aCompanyNames);
                // return sortingMeasure.company === "asc"
                //     ? a.assigned_company.name.localeCompare(b.assigned_company.name)
                //     : b.assigned_company.name.localeCompare(a.assigned_company.name);
            } else {
                return sortingMeasure.email === "asc"
                    ? a.email.localeCompare(b.email)
                    : b.email.localeCompare(a.email);
            }
        });
    };

    let listItems = typeof searchVal.value !== "undefined" && searchVal.value !== "" ? applyFilter() : userRecords;

    let userListitems =
        listItems.length !== 0 ? sortedResults(listItems).slice(paginationStartValue, paginationEndValue) : [];

    const closeModal = () => {
        setModalDetails({});
        setIsModalOpen(false);
        resetUserData();
    };

    const openModal = (modalFor, userData) => {
        setModalDetails({
            openFor: modalFor,
            user_data: userData,
        });
        setIsModalOpen(true);
    };

    const editAction = (user) => {
        return (
            <HtmlTooltip title={<Typography variant="body4">Edit</Typography>}>
                <a
                    href="javacript:void(0)"
                    aria-label={"edit user for " + user.username}
                    onClick={(e) => {
                        e.preventDefault();
                        openModal("update", user);
                    }}
                >
                    <Edit sx={{ color: "#AF3241" }} />
                </a>
            </HtmlTooltip>
        );
    };

    const deleteAction = (user) => {
        return (
            <HtmlTooltip title={<Typography variant="body4">Delete</Typography>}>
                <a
                    href="javacript:void(0)"
                    aria-label={"delete user for " + user.username}
                    onClick={(e) => {
                        e.preventDefault();
                        if (user.multiple_accounts === true) {
                            setShowMultiCompanyConfirmation(true);
                        } else {
                            setShowConfirmation(true);
                        }
                        setUserToDelete(user);
                    }}
                >
                    <Delete sx={{ color: "#AF3241" }} />
                </a>
            </HtmlTooltip>
        );
    };

    const disabledEditAction = (user) => {
        return (
            <HtmlTooltipDisabled title={"No Access"}>
                <a
                    href="javacript:void(0)"
                    onClick={(e) => {
                        e.preventDefault();
                    }}
                >
                    <Edit sx={{ color: "#E6E6E6" }} />
                </a>
            </HtmlTooltipDisabled>
        );
    };

    const disabledDeleteAction = (user) => {
        return (
            <HtmlTooltipDisabled title={"No Access"}>
                <a
                    href="javacript:void(0)"
                    onClick={(e) => {
                        e.preventDefault();
                    }}
                >
                    <Delete sx={{ color: "#E6E6E6" }} />
                </a>
            </HtmlTooltipDisabled>
        );
    };

    const handleSortingParamChange = (paramName) => {
        setSortingMeasure((prevState) => {
            return {
                ...prevState,
                [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
            };
        });
        setSortingParamName(paramName);
    };

    const columns = React.useMemo(() => {
        let cols = [
            {
                id: "Favorite",
                // headerAlign: "center",
                // align: "center",
                header: (
                    <ListingHeaderContent>
                        Favorite
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellLeft>
                        {
                            canAssignOrMarkFavorite(user) ?
                                user.favourited === true
                                    ?
                                    <Star
                                        fontSize="small"
                                        onClick={() => {
                                            markFavoriteUser(user.email).then((info) => {
                                                if (info.success === true) {
                                                    toast("User removed from favorites", { type: "success" });
                                                } else {
                                                    toast("Sorry, something went wrong", { type: "error" });
                                                }
                                            });
                                        }}
                                        sx={{ color: "#AF3241", cursor: "pointer" }}
                                    />
                                    :
                                    <StarBorder
                                        fontSize="small"
                                        onClick={() => {
                                            markFavoriteUser(user.email).then((info) => {
                                                if (info.success === true) {
                                                    toast("User marked as favorite", { type: "success" });
                                                } else {
                                                    toast("Sorry, something went wrong", { type: "error" });
                                                }
                                            });
                                        }}
                                        sx={{ color: "#AF3241", cursor: "pointer" }}
                                    />
                                :
                                <StarBorder
                                    fontSize="small"
                                    sx={{ color: "#62626E", cursor: "not-allowed" }}
                                />
                        }
                    </ListingCellLeft>
                ),
            },
            {
                id: "email",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("email")} style={{ cursor: "pointer" }}>
                        E-mail  <SortIcon direction={sortingMeasure.email} />
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellLeft>
                        {user.email}
                    </ListingCellLeft>
                ),
            }
        ];

        if (companyAllowedUserTypes.includes(checkUserDetails.user_detail.user_type)) {
            cols.push({
                id: "company",
                headerAlign: "center",
                align: "center",
                header: (
                    <ListingHeaderContent>
                        Company
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellCenter>
                        <ListingIconMetric>
                            <HtmlTooltip
                                title={
                                    <Box width={350}>
                                        <ul>
                                            {user.assigned_company_details.map((company, index) => {
                                                return (
                                                    <li key={company.id + "_" + company.name + "_" + index} style={{ marginTop: "5px" }}>
                                                        {company.name} - ({company?.invited_by ?? "N/A"})
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </Box>
                                }
                            >
                                <Business sx={{ color: "#AF3241" }} />
                                <ListingMetricValue>
                                    {user.assigned_company_details.length}
                                </ListingMetricValue>
                            </HtmlTooltip>
                        </ListingIconMetric >
                    </ListingCellCenter >
                ),
            });
        } else {
            cols.push(
                {
                    id: "invited_by",
                    header: (
                        <ListingHeaderContent onClick={() => handleSortingParamChange("invited_by")} style={{ cursor: "pointer" }}>
                            Invited By <SortIcon direction={sortingMeasure.invited_by} />
                        </ListingHeaderContent>
                    ),
                    render: (user) => (
                        <ListingCellLeft>
                            {typeof user.invited_by !== "undefined" ? user.invited_by : ""}
                        </ListingCellLeft>
                    ),
                }
            )
        }

        cols = [
            ...cols,
            {
                id: "actions",
                headerAlign: "right",
                align: "right",
                header: (
                    <ListingHeaderContent>
                        Action
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellRight>
                        {/* ---- edit/delete actions if allowed ------ */}
                        {
                            typeof currentStateData.role_name_arr !== "undefined" && currentStateData.role_name_arr.length !== 0 && (
                                <>
                                    {/* ---- edit/delete actions if allowed ------ */}
                                    {permissionScope.user_list_can_edit_brand === true && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                                        ? editAction(user)
                                        : disabledEditAction(user)}
                                    {/* ----- delete ----- */}
                                    {permissionScope.user_list_can_delete_brand === true && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                                        ? deleteAction(user)
                                        : disabledDeleteAction(user)}
                                </>
                            )
                        }
                    </ListingCellRight>
                ),
            }
        ]

        return cols;
    }, [sortingMeasure]);

    React.useEffect(() => {
        //needed to validate if edit/delete actions are allowed. If the assigned user role is not in the list of allowed roles, then the actions will be disabled -- fetchRoles are already loaded through users page so we don't need to call it again
        // fetchRoles(false);

        let filterCookieKeyword = "brand_users_filter";

        // check if filter cookie is set and set it in redux state
        if (typeof document.cookie.split("; ").find((row) => row.startsWith(filterCookieKeyword)) !== "undefined") {
            const filterObj = JSON.parse(
                document.cookie
                    .split("; ")
                    .find((row) => row.startsWith(filterCookieKeyword))
                    .split("=")[1]
            );
            setSearchVal({ value: filterObj });
        }

        // reset pagination redux state
        manageUsersPaginationCookie({
            user_list_start: 0,
            user_list_end: 10,
        });

    }, [usedFor]);

    return (
        <>
            {/* ============ Action buttons =========== */}
            <Stack direction="row" spacing={2}>
                <ActionButton
                    onClick={(e) => {
                        e.preventDefault();
                        openModal("add", {});
                    }}
                    label={"Add Brand User"}
                    icon={<Add />}
                />
            </Stack>
            <Box height={30} />
            <Box width={350}>
                <CustomTextInput
                    fullWidth
                    variant="standard"
                    placeholder="Search"
                    label="Search"
                    autoComplete="off"
                    type={"text"}
                    value={searchVal.value}
                    onChange={(e) => {
                        if (e.target.value === "") {
                            deleteCookie("brand_users_filter", true);
                        }
                        setSearchVal({ value: e.target.value });
                    }}
                    onBlur={(e) => { }}
                />
            </Box>

            {/* ============== users listing ============ */}
            <Box height={30} />
            <ListingDataTable
                columns={columns}
                rows={userListitems}
                rowKey={(user) => user.id}
                emptyText="No users found"
                loading={typeof currentStateData.brand_users_list === "undefined" && userRecords.length === 0 ? true : false}
            />
            {/* --------- record count info and pagination --------- */}
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mt={2} mb={5}>
                <Pagination
                    totalCount={listItems.length}
                    reduxPaginationKeyword="users_pagination"
                    reduxStartKeyword="user_list_start"
                    reduxEndKeyword="user_list_end"
                    useWindowScroll={true}
                />
            </Stack>


            {/* ============ modal popup for add/update User ========== */}
            {isModalOpen && (
                <AddBrandUser
                    open={isModalOpen}
                    onClose={closeModal}
                    size={"md"}
                    closeModal={closeModal}
                    usedFor={modalDetails.openFor}
                    userInfo={modalDetails.user_data}
                    cyreenBoolean={checkUserDetails?.user_detail?.user_type === "cyreen" ? true : false}
                />
            )}
            {/* ---------- delete Confirmation ---------- */}
            {showConfirmation === true && (
                <Confirmation
                    confirmationActionCall={() => {
                        deleteUser(userToDelete).then((info) => {
                            if (info.success === true) {
                                setUserToDelete({});
                                setShowConfirmation(false);
                                toast("User deleted successfully ", { type: "success" });
                            } else {
                                toast("Sorry, something went wrong", { type: "error" });
                            }
                        });
                    }}
                    closePopup={(e) => {
                        setUserToDelete({});
                        setShowConfirmation(false);
                    }}
                    showConfirmationPopup={showConfirmation}
                    confirmationTitle={"Delete Brand: " + userToDelete.username}
                    confirmationMessage={
                        <>
                            <Typography variant="body2">
                                You are deleting a brand account. This action is permanent and cannot be reversed.
                            </Typography>
                            <Box height={20} />
                            <Typography variant="body2">Are you sure you want to proceed?</Typography>
                        </>
                    }
                />
            )}
            {/* ------------ delete confirmation for user with multiple companies ------ */}
            {showMultiCompanyConfirmation === true && (
                <Confirmation
                    confirmationActionCall={() => {
                        setFieldError({});
                        if (typeof userToDeleteFromCompany.id === "undefined") {
                            setFieldError({ ...fieldError, user_company: "Please select a company" });
                            return;
                        } else {
                            deleteUserFromSpecificCompany(userToDelete, userToDeleteFromCompany.id).then((info) => {
                                if (info.success === true) {
                                    setUserToDelete({});
                                    setShowMultiCompanyConfirmation(false);
                                    setUserToDeleteFromCompany({});
                                    setFieldError({});
                                    toast("User deleted successfully", { type: "success" });
                                } else {
                                    toast("Sorry, something went wrong", { type: "error" });
                                }
                            });
                        }

                    }}
                    closePopupOnClose={false}
                    closePopup={(e) => {
                        setUserToDelete({});
                        setShowMultiCompanyConfirmation(false);
                        setUserToDeleteFromCompany({});
                        setFieldError({});
                    }}
                    showConfirmationPopup={showMultiCompanyConfirmation}
                    confirmationTitle={"Delete Brand: " + userToDelete.username}
                    confirmationMessage={
                        <>
                            <SingleSelect
                                enableFullWidth={true}
                                label={<span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    Company Selection* <HtmlTooltip title={
                                        <Box width={200}>
                                            <Typography variant="body2">
                                                Delete brand accounts for your company or any sub-company.
                                                Select the company for which you want to delete this brand account.
                                            </Typography>
                                        </Box>
                                    }>
                                        <Help fontSize="small" />
                                    </HtmlTooltip>
                                </span>}
                                selectionCheck={(option, value) => option.label === value.label}
                                sortOptions={false}
                                options={
                                    typeof userToDelete.assigned_company_details !== "undefined" ? userToDelete.assigned_company_details.map(companyDtl => {
                                        return { id: companyDtl.id, label: companyDtl.name };
                                    }) : []
                                }
                                defaultVal={userToDeleteFromCompany}
                                onChangeCall={(selectedOption) => {
                                    setUserToDeleteFromCompany(selectedOption);
                                }}
                                fieldError={
                                    typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                                        ? fieldError.user_company
                                        : ""
                                }
                            />
                            <Box height={20} />
                            <Typography variant="body2">
                                You are deleting a brand account. This action is permanent and cannot be reversed.
                            </Typography>
                            <Box height={20} />
                            <Typography variant="body2">Are you sure you want to proceed?</Typography>
                        </>
                    }
                />
            )}
        </>
    );
};

export default BrandUserlist;
